import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  root: {
    background: "#0c0c1e",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    color: "#fff",
    fontFamily: "-apple-system, 'Segoe UI', Helvetica, sans-serif",
    overflow: "hidden",
  },
  header: {
    padding: "10px 18px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(255,255,255,.04)",
    borderBottom: "1px solid rgba(255,255,255,.08)",
    flexShrink: 0,
    flexWrap: "wrap",
    minHeight: 46,
  },
  logoText: { color: "#545cd8", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" },
  badge: {
    background: "rgba(84,92,216,.18)",
    color: "#8b93e8",
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    border: "1px solid rgba(84,92,216,.3)",
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  backBtn: {
    background: "rgba(255,255,255,.07)",
    border: "1px solid rgba(255,255,255,.12)",
    color: "rgba(255,255,255,.7)",
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 5,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  chapterTabs: { display: "flex", gap: 5, marginLeft: "auto", flexWrap: "wrap" },
  chTab: (active) => ({
    background: active ? "rgba(84,92,216,.45)" : "rgba(255,255,255,.07)",
    border: `1px solid ${active ? "#545cd8" : "rgba(255,255,255,.1)"}`,
    color: active ? "#fff" : "rgba(255,255,255,.65)",
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 4,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
  }),
  viewer: {
    flex: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#060610",
    minHeight: 0,
  },
  frameImg: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" },
  chapterOverlay: {
    position: "absolute",
    bottom: 14,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(12,12,22,.9)",
    backdropFilter: "blur(12px)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".04em",
    padding: "7px 18px 7px 13px",
    borderRadius: 7,
    borderLeft: "4px solid #545cd8",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
  // Segmented chapter timeline bar
  timelineWrap: {
    height: 8,
    background: "rgba(255,255,255,.06)",
    position: "relative",
    flexShrink: 0,
    cursor: "pointer",
  },
  controlsWrap: {
    padding: "8px 18px 12px",
    flexShrink: 0,
    borderTop: "1px solid rgba(255,255,255,.06)",
  },
  scrubber: { width: "100%", accentColor: "#545cd8", marginBottom: 8, display: "block" },
  btnRow: { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" },
  btn: (active) => ({
    background: active ? "rgba(84,92,216,.4)" : "rgba(255,255,255,.08)",
    border: `1px solid ${active ? "rgba(84,92,216,.6)" : "rgba(255,255,255,.12)"}`,
    color: "#fff",
    padding: "5px 13px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 12,
    transition: "background .15s",
    userSelect: "none",
    whiteSpace: "nowrap",
  }),
  frameCounter: {
    fontSize: 11,
    color: "rgba(255,255,255,.4)",
    marginLeft: "auto",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  hint: { fontSize: 10, color: "rgba(255,255,255,.2)", whiteSpace: "nowrap" },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: 16,
    color: "rgba(255,255,255,.5)",
    fontSize: 14,
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

const FPS_OPTIONS = [1, 3, 6, 12];

export default function VideoPreviewPage() {
  const { id } = useParams();
  const history = useHistory();

  const [manifest, setManifest]       = useState(null);
  const [loadError, setLoadError]     = useState(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [playing, setPlaying]         = useState(false);
  const [fps, setFps]                 = useState(3);

  const timerRef = useRef(null);
  const imgRef   = useRef(null);

  // ── Load manifest ──────────────────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`/preview/${id}/manifest`)
      .then((r) => {
        setManifest(r.data);
        // Preload first 15 frames so initial playback is smooth
        r.data.frames.slice(0, 15).forEach((f) => {
          const img = new Image();
          img.src = `/api/preview/${id}/frame/${f.filename}`;
        });
      })
      .catch(() => setLoadError("Preview not found. It may still be generating — try refreshing in a moment."));
  }, [id]);

  // ── Preload ahead of playhead ──────────────────────────────────────────────
  useEffect(() => {
    if (!manifest) return;
    for (let i = currentFrame + 1; i < Math.min(currentFrame + 8, manifest.total_frames); i++) {
      const img = new Image();
      img.src = `/api/preview/${id}/frame/${manifest.frames[i].filename}`;
    }
  }, [currentFrame, manifest, id]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!manifest) return;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      if (e.key === "ArrowLeft")  { setPlaying(false); setCurrentFrame((f) => Math.max(0, f - 1)); }
      if (e.key === "ArrowRight") { setPlaying(false); setCurrentFrame((f) => Math.min(manifest.total_frames - 1, f + 1)); }
      if (e.key >= "1" && e.key <= "9") {
        const ch = manifest.chapters[+e.key - 1];
        if (ch) { setPlaying(false); setCurrentFrame(ch.frame_index); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [manifest]);

  // ── Playback timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!playing || !manifest) return;
    timerRef.current = setInterval(() => {
      setCurrentFrame((f) => {
        if (f >= manifest.total_frames - 1) { setPlaying(false); return f; }
        return f + 1;
      });
    }, 1000 / fps);
    return () => clearInterval(timerRef.current);
  }, [playing, fps, manifest]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const activeChapter = useCallback(() => {
    if (!manifest) return null;
    return [...manifest.chapters].reverse().find((ch) => ch.frame_index <= currentFrame) || null;
  }, [manifest, currentFrame]);

  const goTo = useCallback((n) => {
    if (!manifest) return;
    setPlaying(false);
    setCurrentFrame(Math.max(0, Math.min(manifest.total_frames - 1, n)));
  }, [manifest]);

  // ── Timeline click ─────────────────────────────────────────────────────────
  const onTimelineClick = (e) => {
    if (!manifest) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    goTo(Math.round(pct * (manifest.total_frames - 1)));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div style={S.root}>
        <div style={S.header}>
          <span style={S.logoText}>◆ {appName} Preview</span>
        </div>
        <div style={S.center}>
          <div style={{ fontSize: 32, opacity: 0.3 }}>⚠</div>
          <div>{loadError}</div>
          <button style={S.btn(false)} onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div style={S.root}>
        <div style={S.header}>
          <span style={S.logoText}>◆ {appName} Preview</span>
          <span style={S.badge}>{id}</span>
        </div>
        <div style={S.center}>
          <Spinner color="primary" />
          <div>Loading preview…</div>
        </div>
      </div>
    );
  }

  const frame   = manifest.frames[currentFrame];
  const chapter = activeChapter();

  return (
    <div style={S.root}>
      {/* ── Header ── */}
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => history.goBack()}>← Back</button>
        <span style={S.logoText}>◆ {appName} Preview</span>
        <span style={S.badge} title={id}>{id}</span>

        <div style={S.chapterTabs}>
          {manifest.chapters.map((ch, i) => (
            <button
              key={i}
              style={S.chTab(chapter?.label === ch.label)}
              onClick={() => goTo(ch.frame_index)}
              title={`Jump to: ${ch.label} (key: ${i + 1})`}
            >
              {ch.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Frame viewer ── */}
      <div style={S.viewer}>
        <img
          ref={imgRef}
          src={`/api/preview/${id}/frame/${frame.filename}`}
          style={S.frameImg}
          alt={`Frame ${currentFrame + 1}`}
          draggable={false}
        />
        {chapter && (
          <div style={S.chapterOverlay}>{chapter.label}</div>
        )}
      </div>

      {/* ── Chapter timeline ── */}
      <div style={S.timelineWrap} onClick={onTimelineClick}>
        {manifest.chapters.map((ch, i) => {
          const next  = manifest.chapters[i + 1];
          const start = (ch.frame_index / manifest.total_frames) * 100;
          const end   = ((next ? next.frame_index : manifest.total_frames) / manifest.total_frames) * 100;
          const active = chapter?.label === ch.label;
          return (
            <div
              key={i}
              title={ch.label}
              style={{
                position: "absolute",
                left: `${start}%`,
                width: `${end - start}%`,
                height: "100%",
                background: active
                  ? "rgba(84,92,216,.75)"
                  : i % 2 === 0
                    ? "rgba(84,92,216,.35)"
                    : "rgba(84,92,216,.2)",
                borderRight: "1px solid rgba(84,92,216,.25)",
                transition: "background .2s",
              }}
            />
          );
        })}
        {/* Playhead */}
        <div
          style={{
            position: "absolute",
            left: `${(currentFrame / Math.max(1, manifest.total_frames - 1)) * 100}%`,
            top: -2,
            width: 3,
            height: 12,
            background: "#fff",
            borderRadius: 2,
            transform: "translateX(-50%)",
            pointerEvents: "none",
            boxShadow: "0 0 4px rgba(255,255,255,.6)",
          }}
        />
      </div>

      {/* ── Controls ── */}
      <div style={S.controlsWrap}>
        {/* Scrubber */}
        <input
          type="range"
          min={0}
          max={manifest.total_frames - 1}
          value={currentFrame}
          onChange={(e) => goTo(+e.target.value)}
          style={S.scrubber}
        />

        <div style={S.btnRow}>
          <button style={S.btn(false)} onClick={() => goTo(currentFrame - 1)} title="← key">◀</button>
          <button
            style={S.btn(playing)}
            onClick={() => setPlaying((p) => !p)}
            title="Space"
          >
            {playing ? "⏸ Pause" : "▶ Play"}
          </button>
          <button style={S.btn(false)} onClick={() => goTo(currentFrame + 1)} title="→ key">▶</button>

          {FPS_OPTIONS.map((f) => (
            <button
              key={f}
              style={S.btn(fps === f)}
              onClick={() => setFps(f)}
              title={`${f} frames per second`}
            >
              {f}fps
            </button>
          ))}

          <span style={S.frameCounter}>
            {currentFrame + 1} / {manifest.total_frames}
          </span>
          <span style={S.hint}>Space · ← → · 1–9 chapter jump</span>
        </div>
      </div>
    </div>
  );
}
